import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    onChange("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={520}
        height={170}
        data-testid="signature-canvas"
        className="w-full border-2 border-dashed border-zinc-600 rounded-lg bg-zinc-900 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-zinc-500">{empty ? "Dessine ta signature ci-dessus" : "Signature saisie ✓"}</span>
        <button onClick={clear} data-testid="clear-signature-btn" className="text-xs text-zinc-400 hover:text-red-400 underline">Effacer</button>
      </div>
    </div>
  );
}

export default function SignContractPublic() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sig, setSig] = useState("");
  const [typedName, setTypedName] = useState("");
  const [accepte, setAccepte] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { payment_link }

  useEffect(() => {
    if (!token) { setErr("Lien invalide."); return; }
    axios.get(`${API}/public/contract/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.already_signed) setDone({ payment_link: null, already: true });
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Lien de contrat invalide ou expiré."));
  }, [token]);

  const submit = async () => {
    if (!accepte) return toast.error("Tu dois accepter les conditions pour signer.");
    if (!sig && !typedName.trim()) return toast.error("Dessine ta signature ou tape ton nom.");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/contract/${token}/sign`, {
        signature: sig || "", typed_name: typedName.trim(), accepted: accepte,
      });
      setDone({ payment_link: r.data.payment_link });
      toast.success("Contrat signé ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signature impossible.");
    } finally { setSubmitting(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6" data-testid="sign-contract-error">
      <div className="text-center">
        <div className="text-4xl mb-3">🥋</div>
        <p className="text-lg font-semibold">{err}</p>
        <p className="text-zinc-500 text-sm mt-2">Contacte le dojo pour recevoir un nouveau lien.</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center" data-testid="sign-contract-loading">
      <p className="text-zinc-400">Chargement du contrat…</p>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6" data-testid="sign-contract-done">
      <div className="max-w-md text-center">
        <div className="text-5xl mb-4">✅</div>
        <h1 className="text-2xl font-bold mb-2">{done.already ? "Contrat déjà signé" : "Contrat signé, merci !"}</h1>
        <p className="text-zinc-400 text-sm mb-6">
          {done.already ? "Ton contrat est déjà enregistré." : "Ta signature électronique a bien été enregistrée."}
          {" "}Dernière étape : active ton mode de paiement (facturé aux 4 semaines, sécurisé Stripe).
        </p>
        {done.payment_link ? (
          <a href={done.payment_link} data-testid="activate-payment-btn"
             className="inline-block bg-red-600 hover:bg-red-500 text-white font-bold px-8 py-4 rounded-lg transition-colors">
            Activer mon paiement →
          </a>
        ) : (
          <p className="text-zinc-500 text-sm">Le dojo te contactera pour l'activation du paiement.</p>
        )}
        <p className="text-zinc-600 text-xs mt-8">Institut Impact Élite Martial · Val-d'Or · Kaizen System</p>
      </div>
    </div>
  );

  const fmt = (n) => (n ? `${Number(n).toFixed(2)} $ CAD / cycle (4 sem.)` : "à confirmer");

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 py-10 px-4" data-testid="sign-contract-page">
      <div className="max-w-xl mx-auto">
        <div className="text-red-500 text-[10px] tracking-[0.4em] mb-1">INSTITUT IMPACT ÉLITE MARTIAL · VAL-D'OR</div>
        <h1 className="text-2xl font-bold mb-1">Contrat d'adhésion</h1>
        <p className="text-zinc-500 text-sm mb-6">Lis les conditions, puis signe électroniquement ci-dessous.</p>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 mb-6" data-testid="contract-summary">
          <div className="flex justify-between py-1.5 border-b border-zinc-800 text-sm"><span className="text-zinc-500">Membre</span><span className="font-medium">{data.member_name}</span></div>
          <div className="flex justify-between py-1.5 border-b border-zinc-800 text-sm"><span className="text-zinc-500">Discipline</span><span className="font-medium">{data.discipline}</span></div>
          <div className="flex justify-between py-1.5 border-b border-zinc-800 text-sm"><span className="text-zinc-500">Fréquence</span><span className="font-medium">{data.sessions_per_week ? `${data.sessions_per_week}×/semaine` : "—"}</span></div>
          <div className="flex justify-between py-1.5 text-sm"><span className="text-zinc-500">Tarif</span><span className="font-medium">{fmt(data.tarif_mensuel)}</span></div>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 mb-6">
          <div className="text-sm font-semibold mb-2">Conditions</div>
          <ul className="space-y-1.5 text-sm text-zinc-300">
            {(data.conditions || []).map((c, i) => <li key={i} data-testid={`condition-${i}`}>• {c}</li>)}
          </ul>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 mb-4">
          <div className="text-sm font-semibold mb-3">Ta signature électronique</div>
          <SignaturePad onChange={setSig} />
          <div className="mt-4">
            <label className="text-xs text-zinc-500">Ou tape ton nom complet</label>
            <input value={typedName} onChange={(e) => setTypedName(e.target.value)} data-testid="typed-name-input"
              placeholder="Prénom Nom" className="w-full mt-1 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-red-500" />
          </div>
        </div>

        <label className="flex items-start gap-2 mb-4 cursor-pointer" data-testid="accepte-label">
          <input type="checkbox" checked={accepte} onChange={(e) => setAccepte(e.target.checked)} data-testid="accepte-checkbox" className="mt-1" />
          <span className="text-sm text-zinc-300">J'ai lu et j'accepte les conditions du présent contrat. Je comprends que ma signature électronique a la même valeur qu'une signature manuscrite.</span>
        </label>

        <button onClick={submit} disabled={submitting || !accepte || (!sig && !typedName.trim())} data-testid="submit-signature-btn"
          className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-bold py-4 rounded-lg transition-colors">
          {submitting ? "Envoi…" : "Signer mon contrat →"}
        </button>
        <p className="text-zinc-600 text-xs text-center mt-6">Signature sécurisée · Institut Impact Élite Martial · Kaizen System</p>
      </div>
    </div>
  );
}
